import { BankIDLink } from "@/components/BankIDLink/BankIDLink";
import { Callout } from "@/components/Callout/Callout";
import { Card } from "@/components/Card/Card";
import { CustomerLayout } from "@/components/CustomerLayout/CustomerLayout";
import { routeLoaderApiClient } from "@/lib/api";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import css from "./index.module.scss";

type DocumentWithtluxCaseTitle = {
  type: "pades" | "sdo";
  tluxCaseTitle: string;
  id: string;
  name: string;
  url: string;
  viewUrl: string;
  signed_at: string | null;
};

export const Route = createFileRoute("/_authenticated-actor/signer/")({
  validateSearch: z.discriminatedUnion("status", [
    z.object({
      status: z.undefined(),
    }),
    z.object({
      status: z.literal("ok"),
    }),
    z.object({
      status: z.literal("feil"),
      type: z.enum(["samtidig-signering", "feil-fodselsnummer"]).optional(),
    }),
  ]),

  loader: async () => {
    const res = await routeLoaderApiClient.signing.$get();

    if (res.ok === false) {
      throw new Error("Failed to fetch signing data");
    }

    return {
      cases: await res.json(),
    };
  },
  component: SignerPage,
});

function SignerPage() {
  const search = Route.useSearch();
  const { cases } = Route.useLoaderData();

  // We have a list of documents per case in case we want to visually group documents by case
  const documents: DocumentWithtluxCaseTitle[] = [];
  for (const caseItem of cases) {
    for (const document of caseItem.documents) {
      documents.push({
        tluxCaseTitle: caseItem.tluxCaseTitle,
        ...document,
      });
    }
  }

  const unsignedDocuments = documents.filter((document) => document.signed_at == null);
  const signedDocuments = documents.filter((document) => document.signed_at != null);

  let unsignedCaseCount = 0;
  for (const caseItem of cases) {
    unsignedCaseCount += caseItem.documents.filter((document) => document.signed_at == null).length;
  }

  return (
    <CustomerLayout>
      <title>Signeringsportal</title>

      <div className="u-flow" data-flow-space="30">
        {search.status != null && <Status {...search} />}

        <div>
          <h1 className={css.heading}>Dokumenter til signering</h1>
          {unsignedDocuments.length > 0 ? (
            <div className="u-flow">
              {unsignedDocuments.map((document) => {
                const signingUrlObject = new URL(document.url, window.location.href);

                return (
                  <Card key={document.id} title={document.name} icon="signature">
                    <div className="u-flow" data-flow-space="10">
                      <p>{document.tluxCaseTitle}</p>
                      <BankIDLink type="sign-with-bankid" href={signingUrlObject.href} />
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <p>Ingen dokumenter til signering</p>
          )}
        </div>

        {signedDocuments.length > 0 && (
          <div>
            <h1 className={css.heading}>Ferdig signerte dokumenter</h1>
            <div className="u-flow">
              {signedDocuments.map((document) => {
                return (
                  <Card key={document.id} title={document.name} icon="signature">
                    <div className="u-flow" data-flow-space="10">
                      <p>{document.tluxCaseTitle}</p>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </CustomerLayout>
  );
}

function Status(props: { status: "ok" } | { status: "feil"; type?: "samtidig-signering" | "feil-fodselsnummer" }) {
  if (props.status === "ok") {
    return (
      <Callout variant="green" icon="key">
        Signering fullført.
      </Callout>
    );
  }

  if (props.status === "feil") {
    switch (props.type) {
      case "samtidig-signering": {
        return (
          <Callout variant="yellow" icon="key">
            Noen andre holder holder på å signere det samme dokumentet. Vennligst prøv igjen senere.
          </Callout>
        );
      }

      case "feil-fodselsnummer": {
        return (
          <Callout variant="red" icon="key">
            Dokumentet ble signert med annet fødselsnummer enn forventet.
          </Callout>
        );
      }
    }
  }

  return (
    <Callout variant="red" icon="key">
      Noe gikk galt under signering. Vennligst prøv igjen.
    </Callout>
  );
}
